import React, { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { AuthContext, ToastContext, CoreFetch, ProfessionalApi, CollectionApi, ProviderUpdateCollectionPaymentPayload, ClaimApi } from '@enter/core';
import { IInjectedRootStore, IObservedRootStore } from 'stores';
import { DialogContext, IDialogContextProps } from 'services';
import { ProviderMessagingContext, IProviderMessagingContextProps, PageLayout, PageLayoutRow, DataTableCard } from 'components';
import { StatementDetailsPage, IStatementDetailsProps } from '@enter/core/src/ui/patient-statement';
import { equalsFilter } from 'stores/util';
import { claimsColumnsBase } from 'pages/main/claims';
import { ProviderClaim } from '@enter/core';

export const StatementDetails: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            
            const { patientId, accountId } = useParams<{ patientId: string; accountId?: string }>();
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            const messagingContext = useContext(ProviderMessagingContext);
            const toastContext = useContext(ToastContext);

            if (!patientId) {
                return <></>;
            }

            const statementDetailsProps: IStatementDetailsProps = useMemo(() => ({
                patientId,
                patientStore: {
                    loadPatientStatement: (id: string) => rootStore.patientStore.loadPatientStatement(id),
                },
                claimStore: {
                    createCollectionPayment: (payload: any) => rootStore.claimStore.createCollectionPayment(payload),
                    updateCollectionPayment: async (id: string, payload: ProviderUpdateCollectionPaymentPayload) => {
                        return await CoreFetch<CollectionApi>(CollectionApi).patchProviderUpdateCollectionPayment({
                            id,
                            providerUpdateCollectionPaymentPayload: {
                                amount: payload.amount,
                                claimId: payload.claimId,
                            },
                        });
                    },
                    deleteCollectionPayment: async (id: string) => {
                        // Provider doesn't have a delete endpoint, so we'll throw an error or do nothing
                        // For now, we'll throw an error to indicate it's not supported
                        throw new Error('Delete collection payment is not supported for Provider');
                    },
                },
                layoutStore: {
                    toggleSidebar: (collapsed: boolean) => rootStore.layoutStore.toggleSidebar(collapsed),
                },
                dialogContext: {
                    showDialog: (props: { props: any; Component: React.ReactNode }) => dialogContext.showDialog(props),
                    hideDialog: () => dialogContext.hideDialog(),
                    showError: (err: Error) => dialogContext.showError(err),
                },
                toastContext,
                messagingContext: {
                    openPanel: (props: any) => messagingContext.openPanel?.(props),
                    closePanel: () => messagingContext.closePanel?.(),
                },
                authContext,
                PageLayout,
                DataTableCard,
                PageLayoutRow,
                equalsFilter,
                fetchProfessionalClaims: async (claimIds: string[]) => {
                    return await CoreFetch<ProfessionalApi>(ProfessionalApi)
                        .listProviderProfessionalClaims({
                            filterClaimId: equalsFilter(claimIds),
                        });
                },
                fetchClaimsByPatientId: async (patientId: string) => {
                    return await CoreFetch<ClaimApi>(ClaimApi)
                        .listProviderClaims({
                            filterPatientId: equalsFilter([patientId]),
                        });
                },
                claimsColumns: claimsColumnsBase,
                getClaimRowHref: (claim: ProviderClaim): string => {
                    return accountId ? `/${accountId}/claims/${claim.id}` : `/claims/${claim.id}`;
                },
            }), [patientId, accountId, rootStore, dialogContext, toastContext, messagingContext, authContext, equalsFilter]);

            return <StatementDetailsPage {...statementDetailsProps} />;
        },
    ),
);

